// Fetches the current store once (GET /me/store) and shares it app-wide.
// Every data page needs the store id (reports/opportunities/contents/tasks are
// all store-scoped), so we resolve it here rather than in each page.

import { createContext, useContext, type ReactNode } from "react";
import { Banner, Card, Layout, Page, Spinner, Text } from "@shopify/polaris";
import { useApi } from "../api/ApiProvider";
import { useAsync, type AsyncState } from "../api/hooks";
import type { MeStore } from "../api/types";

interface StoreContextValue {
  store: MeStore;
  reload: () => void;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const state: AsyncState<MeStore> = useAsync(() => api.getMyStore(), [api]);

  if (state.loading && !state.data) {
    return (
      <Page title="RankVine">
        <Card>
          <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
            <Spinner accessibilityLabel="Loading store" size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  if (state.error || !state.data) {
    return (
      <Page title="RankVine">
        <Layout>
          <Layout.Section>
            <Banner
              tone="critical"
              title="Couldn't load your store"
              // A transient App Bridge/session-token failure (incl. a 401 from
              // /me/store) shouldn't be a dead end. Retry re-runs the loader,
              // which fetches a fresh session token and re-requests — no full
              // page reload needed.
              action={{ content: "Retry", onAction: state.reload }}
            >
              <Text as="p">
                {state.error ??
                  "No store is connected to this session yet. Finish the app install and reopen RankVine."}
              </Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <StoreContext.Provider value={{ store: state.data, reload: state.reload }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore(): StoreContextValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within <StoreProvider>");
  return ctx;
}
